import WebApp from "@twa-dev/sdk";
import { useState } from "react";

import type {
  ApplicantTag,
  ColdSearchResult,
  PipelineStage,
} from "@/types/domain";

import { reportColdSearchSourceOpened } from "@/api/queries";
import WorkUaOpenControls from "@/components/WorkUaOpenControls";
import {
  defaultStage,
  isDismissed,
  originSource,
  sourceLabel,
} from "@/lib/coldSearch";
import { apiBase } from "@/lib/env";
import { safeConfirm } from "@/lib/telegramSafe";
import { useMarkColdCandidateViewed } from "@/lib/useMarkColdCandidateViewed";
import { WORKUA_OPEN_REQUIRED_HINT, needsWorkUaOpen } from "@/lib/workuaOpen";

interface ColdResultActionsProps {
  result: ColdSearchResult;
  vacancyId?: string;
  /** PeopleForce pipeline stages for the cold search's vacancy, already
   * sorted by position. Empty when the backend does not surface them —
   * the «Цікавий» flow then degrades to a candidate-only push. */
  stages: PipelineStage[];
  /** Server-resolved default stage id (position-1 «Новий кандидат»), or
   * ``null`` when no stages are available. */
  defaultStageId: number | null;
  /** Stage name chosen when this result was pushed. The row carries
   * ``pushed_to_pf_at`` but no stage, so the caller remembers the label
   * locally and it does not survive a full reload. */
  pushedStageName: string | undefined;
  /** ``applicantStateId`` is omitted for a candidate-only push;
   * ``stageName`` is the chosen stage label to show on the card. */
  onSend: (applicantStateId: number | undefined, stageName?: string) => void;
  onDismiss: () => void;
  sending: boolean;
  dismissing: boolean;
  /** PeopleForce «Теги» the recruiter may file this candidate under. Empty
   * while loading or when the tenant has none — the button stays disabled. */
  tags: ApplicantTag[];
  tagsFailed: boolean;
  /** `onFiled` runs once PeopleForce has accepted the filing. */
  onAddToPool: (tagId: number, onFiled: () => void) => void;
  addingToPool: boolean;
  /** May the free Work.ua re-score be offered (see WorkUaOpenControls). */
  mayOpenWorkUa: boolean;
  /** Runs after every «Відкрити контакт» click — the caller re-reads the row. */
  onWorkUaOpenSettled: () => void;
}

/** What a recruiter can do with one cold-search row: open the résumé, buy
 * its Work.ua contact, send to PeopleForce («Цікавий»), dismiss, or file in
 * the talent pool.
 *
 * Returns a fragment: the caller's flex column spaces the controls. Every
 * action is keyed by the row's own (``cold_search_id``, ``id``) pair, which
 * the caller wires into the callbacks. */
export default function ColdResultActions({
  result,
  vacancyId,
  stages,
  defaultStageId,
  pushedStageName,
  onSend,
  onDismiss,
  sending,
  dismissing,
  tags,
  tagsFailed,
  onAddToPool,
  addingToPool,
  mayOpenWorkUa,
  onWorkUaOpenSettled,
}: ColdResultActionsProps): JSX.Element {
  const markViewed = useMarkColdCandidateViewed(vacancyId, result.candidate.id);
  // Без телефону й пошти PeopleForce отримав би людину, якій ніхто не
  // подзвонить; сервер однаково відповість 409 `workua_open_required`.
  const needsOpen = needsWorkUaOpen(result);
  const pushed = result.pushed_to_pf_at !== null;
  const dismissed = isDismissed(result);
  const origin = originSource(result);
  // Prefer the server's default stage id; fall back to the client-side
  // position-1-else-lowest compute if the backend omitted it.
  const fallbackStage =
    stages.find((s) => s.id === defaultStageId) ?? defaultStage(stages);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(
    fallbackStage ? fallbackStage.id : null,
  );

  const chosenStage =
    stages.find((s) => s.id === selectedStageId) ?? fallbackStage;

  const confirmSend = (): void => {
    // With a resolved pipeline stage we place the candidate on it; without
    // stages we fall back to the Phase-1 candidate-only push.
    const prompt =
      chosenStage !== null
        ? `Додати кандидата «${result.candidate.full_name}» в PeopleForce на етап "${chosenStage.name}"?`
        : `Створити кандидата «${result.candidate.full_name}» в PeopleForce?`;
    safeConfirm(prompt, (confirmed) => {
      if (confirmed) onSend(chosenStage?.id, chosenStage?.name);
    });
  };

  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const chosenTag = tags.find((t) => t.id === selectedTagId) ?? null;
  const inPool = result.talent_pool_at !== null;

  const confirmAddToPool = (): void => {
    if (chosenTag === null) return;
    safeConfirm(
      `Додати «${result.candidate.full_name}» в кадровий резерв PeopleForce з тегом «${chosenTag.name}»? Вакансія прикріплена не буде.`,
      (confirmed) => {
        if (confirmed) onAddToPool(chosenTag.id, () => setSelectedTagId(null));
      },
    );
  };

  return (
    <>
      {result.source_url ? (
        <button
          type="button"
          onClick={() => {
            markViewed();
            // Fire-and-forget: the page must open whether or not the stamp lands.
            void reportColdSearchSourceOpened(
              result.cold_search_id,
              result.id,
            ).catch(() => undefined);
            WebApp.openLink(result.source_url!);
          }}
          className="self-start text-xs text-tg-link underline"
        >
          Відкрити резюме на {sourceLabel(origin)}
        </button>
      ) : (
        result.cv_download_url && (
          // Internal hits have no external permalink but always carry a
          // stored CV. The URL is API-relative (token in query) — prepend
          // the API origin and open it in the external browser.
          <button
            type="button"
            onClick={() => {
              markViewed();
              WebApp.openLink(`${apiBase()}${result.cv_download_url!}`);
            }}
            className="self-start text-xs text-tg-link underline"
          >
            Відкрити CV
          </button>
        )
      )}

      <WorkUaOpenControls
        result={result}
        mayOpen={mayOpenWorkUa}
        onSettled={onWorkUaOpenSettled}
      />

      {pushed && pushedStageName && (
        <div className="text-xs text-tg-hint">
          Надіслано в PF: {pushedStageName}
        </div>
      )}

      {pushed && result.peopleforce_url && (
        // Open the pushed candidate in PeopleForce (/recruitment/applicants/{id}).
        // Survives reload (comes from the API), unlike the in-session stage name.
        <button
          type="button"
          onClick={() => WebApp.openLink(result.peopleforce_url!)}
          className="self-start text-xs text-tg-link underline"
        >
          Відкрити в PeopleForce ↗
        </button>
      )}

      {!pushed && stages.length > 0 && (
        <label className="flex flex-col gap-1 text-xs text-tg-hint">
          Етап у PeopleForce
          <select
            // Bind to the resolved ``chosenStage`` (not raw state) so the
            // default selects correctly even when stages arrive after the
            // card mounts (separate query, no state-sync effect needed).
            value={chosenStage?.id ?? ""}
            onChange={(e) => setSelectedStageId(Number(e.target.value))}
            disabled={sending}
            className="rounded border border-tg-secondary bg-tg-bg p-2 text-sm text-tg-text"
          >
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          onClick={confirmSend}
          disabled={pushed || sending || needsOpen}
          className="flex-1 rounded bg-emerald-600 py-2 text-xs text-white disabled:opacity-50"
        >
          {pushed ? "Надіслано в PF" : sending ? "Надсилаю…" : "Цікавий"}
        </button>
        {/* «Цікавий» лишається активною і на відхиленому рядку: скасування
            відмови ендпоїнта немає, а push у PeopleForce їх не блокує — це
            єдиний шлях назад після помилкового натискання. */}
        <button
          type="button"
          onClick={onDismiss}
          disabled={dismissed || dismissing}
          className="flex-1 rounded border border-tg-secondary py-2 text-xs disabled:opacity-50"
        >
          {dismissed ? "Відхилений" : dismissing ? "Приховую…" : "Не цікавий"}
        </button>
      </div>

      {/* Один рядок на обидві кнопки: на телефоні підказки-тултіпа немає, а
          «В резерв» стоїть просто під «Цікавий». */}
      {needsOpen && (
        <div className="text-xs text-tg-hint">{WORKUA_OPEN_REQUIRED_HINT}</div>
      )}

      {/* Кадровий резерв. Окремий блок, а не третя кнопка поруч: це інше
          рішення — «подобається, але вакансії для нього зараз немає». Лишається
          активним і після «Цікавий», і після попереднього занесення (другий
          хештег на ту саму людину — звичайна річ). */}
      {tags.length > 0 ? (
        <div className="flex gap-2">
          <select
            value={chosenTag?.id ?? ""}
            onChange={(e) =>
              setSelectedTagId(
                e.target.value === "" ? null : Number(e.target.value),
              )
            }
            disabled={addingToPool}
            aria-label="Хештег кадрового резерву"
            className="min-w-0 flex-1 rounded border border-tg-secondary bg-tg-bg p-2 text-xs text-tg-text"
          >
            {/* Без заздалегідь обраного тегу: перший у списку підставлявся
                сам, і «В резерв» в один дотик клав людину під чужу посаду. */}
            <option value="" disabled>
              Оберіть посаду
            </option>
            {tags.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={confirmAddToPool}
            disabled={addingToPool || chosenTag === null || needsOpen}
            className="shrink-0 rounded border border-tg-secondary px-3 py-2 text-xs disabled:opacity-50"
          >
            {addingToPool ? "Додаю…" : "В резерв"}
          </button>
        </div>
      ) : tagsFailed ? (
        // Without this the block simply vanishes on a failed request and the
        // recruiter concludes the feature is not there at all.
        <div className="text-xs text-rose-400">
          Не вдалося прочитати теги з PeopleForce — оновіть екран.
        </div>
      ) : null}

      {inPool && result.talent_pool_tag !== null && (
        <div className="text-xs text-tg-hint">
          У резерві: {result.talent_pool_tag}
        </div>
      )}
    </>
  );
}
